<template>
  <div class="h-100 d-flex flex-column bg-light">
    <!-- 顶部操作与图例导航栏 -->
    <div class="p-2 border-bottom bg-card d-flex align-items-center justify-content-between shadow-sm flex-wrap" style="z-index: 10;">
      <div class="d-flex align-items-center flex-wrap mr-2">
        <span class="font-weight-bold mr-2 text-main d-flex align-items-center" style="font-size: 1.15rem; white-space: nowrap;">
          异动提醒
        </span>

        <!-- 交易日导航器：标准步进切换 + 日期选择器 -->
        <div class="movement-date-stepper d-flex align-items-center mr-3 my-1">
          <button 
            type="button" 
            class="movement-date-btn" 
            title="上一交易日" 
            :disabled="loading"
            @click="stepTradeDate(-1)"
          >
            <b-icon icon="chevron-left" font-scale="0.85"></b-icon>
          </button>
          
          <b-form-datepicker
            v-model="date1"
            locale="zh"
            class="movement-datepicker border-0 mx-1"
            size="sm"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }"
            style="width: 155px;"
            :max="maxSelectableDate"
          ></b-form-datepicker>

          <button 
            type="button" 
            class="movement-date-btn" 
            title="下一交易日" 
            :disabled="loading || isLatestDate"
            @click="stepTradeDate(1)"
          >
            <b-icon icon="chevron-right" font-scale="0.85"></b-icon>
          </button>
        </div>

        <!-- 视觉图例与说明提示 -->
        <div class="d-flex align-items-center text-muted small flex-wrap my-1">
          <span class="mr-2 d-flex align-items-center"><span class="legend-dot dot-danger"></span>已达异动</span>
          <span class="mr-2 d-flex align-items-center"><span class="legend-dot dot-warning-high"></span>临界预警(&le;3%)</span>
          <span class="mr-2 d-flex align-items-center"><span class="legend-dot dot-warning-mild"></span>观察区(&le;涨停)</span>
          <span class="mr-2 d-flex align-items-center"><span class="legend-dot dot-safe"></span>安全(涨停不触)</span>
          <span class="d-flex align-items-center ml-1">
            <b-icon icon="info-circle" class="text-secondary cursor-pointer mr-1" id="popover-movement-rules"></b-icon>
            <span class="cursor-pointer text-secondary" id="popover-movement-rules-text">规则说明</span>
          </span>
          <b-popover target="popover-movement-rules" triggers="hover focus" placement="bottom" custom-class="rule-popover-box">
            <template #title>异动监控规则说明</template>
            <div style="font-size: 12px; line-height: 1.6;">
              <p class="mb-1"><strong>异动1</strong>：连续 10 个交易日内涨幅偏离值累计达到 100%。</p>
              <p class="mb-1"><strong>异动2</strong>：连续 30 个交易日内涨幅偏离值累计达到 200%。</p>
              <p class="mb-1"><strong>今日触发与“距”</strong>：展示今日收盘若触及异动所需的涨幅与价格；“距 X%”代表距触发还差的百分点；超出今日涨停限制（主板10%/双创20%）无法触及的标记为“安全”。</p>
              <hr class="my-1">
              <p class="mb-0 text-muted">{{ disclaimerText }}</p>
            </div>
          </b-popover>
        </div>
      </div>

      <!-- 右侧筛选与工具按钮 -->
      <div class="d-flex align-items-center my-1">
        <b-form-radio-group
          v-model="filterType"
          :options="filterOptions"
          buttons
          button-variant="outline-primary"
          size="sm"
          class="mr-2"
        ></b-form-radio-group>
        <WxShare title="异动提醒 - 自在量化" desc="连续10个/30个交易日内涨幅偏离值累计达到100%/200%的股票异动监控" class="mr-2" />
        <b-button variant="outline-primary" size="sm" @click="fetchList" class="border-0 mr-1" title="刷新数据">
          <b-icon icon="arrow-clockwise"></b-icon>
        </b-button>
        <b-button variant="outline-primary" size="sm" @click="downloadScreenshot" class="border-0" title="导出截图">
          <b-icon icon="download"></b-icon>
        </b-button>
      </div>
    </div>

    <!-- 表格内容主体 -->
    <div class="flex-grow-1 bg-main overflow-auto" ref="tableContainer">
      <div v-if="loading" class="text-center p-5">
        <b-spinner small variant="primary"></b-spinner>
      </div>
      <div v-else-if="items.length===0" class="text-center p-5 text-muted">暂无数据</div>
      <div v-else>
        <!-- Desktop View (桌面端优化表格) -->
        <div v-if="!isMobile" class="px-3 pt-2">
         <div style="max-width: 1440px;" class="mx-auto w-100">
           <!-- 复盘业务状态提示条 -->
           <div v-if="date1" class="replay-status-bar mb-2 px-3 py-1-5 rounded d-flex align-items-center justify-content-between flex-wrap" :class="statusBarThemeClass">
             <div class="d-flex align-items-center flex-wrap">
               <span class="badge mr-2 font-weight-bold" :class="statusBadgeClass">
                 {{ statusBadgeText }}
               </span>
               <span class="replay-status-text">
                 <template v-if="isRealTrading">
                   ⚡ 交易日盘中实时监控：基于上一交易日（<strong>{{ baseTradeDate || latestTradeDate }}</strong>）预警名单，实时跟踪盘中最新价格与涨跌幅，动态测算距异动阈值空间。
                 </template>
                 <template v-else-if="isForecast">
                   🎯 查看目标交易日（<strong>{{ targetTradeDate || date1 }}</strong>）开盘前预警：以最新收盘基准日（<strong>{{ baseTradeDate || latestTradeDate }}</strong>）日K推演，测算开盘后触发异动所需涨幅与价格。
                 </template>
                 <template v-else>
                   📜 历史已收盘回放：以 <strong>{{ baseTradeDate || date1 }}</strong> 收盘为基准推演次日，右侧【次日实况】为 <strong>{{ targetTradeDate }}</strong> 真实收盘表现，对比验证是否触及异动。
                 </template>
               </span>
             </div>
             <div class="text-muted small ml-auto" v-if="items.length > 0">
               共监控 <strong>{{ items.length }}</strong> 只高位/重点关注股票
             </div>
           </div>

           <b-table
             :items="mergedItems"
             :fields="dynamicFields"
             striped
             hover
             small
             head-variant="light"
             class="mb-0 border shadow-sm rounded sticky-header-table movement-table"
           >
            <!-- 股票代码与名称 -->
            <template #cell(symbol)="data">
              <div class="font-weight-bold" style="white-space: nowrap; cursor: pointer;" @click="openSidebar(data.item)">
                <div :class="data.item.is_monitored ? 'text-danger' : 'text-main'" class="d-flex align-items-center stock-name-row">
                  <span>{{ data.item.symbol_name || '' }}</span>
                  <span v-if="data.item.is_monitored" class="badge badge-danger ml-1 cursor-help" style="font-size: 10px; padding: 2px 4px;" v-b-tooltip.hover.top="disclaimerText">异</span>
                  <span v-else-if="data.item.monitor_expired" class="badge badge-secondary ml-1 cursor-help" style="font-size: 10px; padding: 2px 4px;" v-b-tooltip.hover.top="disclaimerText">异</span>
                </div>
                <div class="stock-code-text font-monospace">{{ data.item.symbol_code }}</div>
                <div v-if="data.item.is_monitored || data.item.monitor_expired" class="small mt-1" style="font-size: 11px;" :class="data.item.is_monitored ? 'text-danger' : 'text-muted'">
                   {{ data.item.monitor_start_date }}~{{ data.item.monitor_end_date }}
                   <span v-if="data.item.monitor_expired">(已到期)</span>
                </div>
              </div>
            </template>

            <!-- 累计涨幅 (带清晰 10日 / 30日 胶囊) -->
            <template #cell(gain)="data">
              <div v-if="data.item.gain100 !== null" class="mb-1 d-flex align-items-center justify-content-end">
                <span class="badge-tag badge-tag-10 mr-1">10日</span>
                <span class="font-weight-bold cell-main-val font-monospace">{{ formatPct(data.item.gain100) }}</span>
              </div>
              <div v-if="data.item.gain200 !== null" class="d-flex align-items-center justify-content-end">
                <span class="badge-tag badge-tag-30 mr-1">30日</span>
                <span class="font-weight-bold cell-main-val font-monospace">{{ formatPct(data.item.gain200) }}</span>
              </div>
            </template>

            <!-- 天数 -->
            <template #cell(days)="data">
              <div v-if="data.item.days1" class="mb-1 cell-sub-val text-center font-monospace">{{ data.item.days1 }}天</div>
              <div v-if="data.item.days2" class="cell-sub-val text-center font-monospace">{{ data.item.days2 }}天</div>
            </template>

            <!-- 今日触发 (横向舒展排版：微标 + 触发涨幅/价格 + 距差醒目胶囊) -->
            <template #cell(trigger_today)="data">
              <!-- 10日触发 -->
              <div v-if="data.item.gain_to_100 !== null"
                   :class="getTodayStyleClass(data.item.gain_to_100, data.item.px_change_rate, data.item.symbol_code)"
                   class="trigger-cell-box mb-1">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <span class="badge-tag badge-tag-10 mr-2">10日</span>
                    <span class="trigger-gain font-monospace mr-2">{{ formatPct(data.item.gain_to_100) }}</span>
                    <span class="trigger-price font-monospace">({{ formatPrice(data.item.price_to_100) }})</span>
                  </div>
                  <span class="trigger-diff-badge font-monospace" 
                        :class="getDiffClass(data.item.gain_to_100, data.item.px_change_rate, data.item.symbol_code)"
                        :title="getDiffTooltip(data.item.gain_to_100, data.item.px_change_rate, data.item.symbol_code)">
                    {{ getDiffText(data.item.gain_to_100, data.item.px_change_rate, data.item.symbol_code) }}
                  </span>
                </div>
              </div>
              <!-- 30日触发 -->
              <div v-if="data.item.gain_to_200 !== null"
                   :class="getTodayStyleClass(data.item.gain_to_200, data.item.px_change_rate, data.item.symbol_code)"
                   class="trigger-cell-box">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <span class="badge-tag badge-tag-30 mr-2">30日</span>
                    <span class="trigger-gain font-monospace mr-2">{{ formatPct(data.item.gain_to_200) }}</span>
                    <span class="trigger-price font-monospace">({{ formatPrice(data.item.price_to_200) }})</span>
                  </div>
                  <span class="trigger-diff-badge font-monospace" 
                        :class="getDiffClass(data.item.gain_to_200, data.item.px_change_rate, data.item.symbol_code)"
                        :title="getDiffTooltip(data.item.gain_to_200, data.item.px_change_rate, data.item.symbol_code)">
                    {{ getDiffText(data.item.gain_to_200, data.item.px_change_rate, data.item.symbol_code) }}
                  </span>
                </div>
              </div>
            </template>

            <!-- 明日触发 -->
            <template #cell(trigger_tomorrow)="data">
              <div v-if="data.item.gain_to_100_2 !== null"
                   :class="getFutureStyleClass(data.item.gain_to_100_2)"
                   class="trigger-cell-box mb-1">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <span class="badge-tag badge-tag-10 mr-2">10日</span>
                    <span class="trigger-gain font-monospace mr-2">{{ formatPct(data.item.gain_to_100_2) }}</span>
                  </div>
                  <span class="trigger-price font-monospace">{{ formatPrice(data.item.price_to_100_2) }}</span>
                </div>
              </div>
              <div v-if="data.item.gain_to_200_2 !== null"
                   :class="getFutureStyleClass(data.item.gain_to_200_2)"
                   class="trigger-cell-box">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <span class="badge-tag badge-tag-30 mr-2">30日</span>
                    <span class="trigger-gain font-monospace mr-2">{{ formatPct(data.item.gain_to_200_2) }}</span>
                  </div>
                  <span class="trigger-price font-monospace">{{ formatPrice(data.item.price_to_200_2) }}</span>
                </div>
              </div>
            </template>

            <!-- 后日触发 -->
            <template #cell(trigger_day_after)="data">
              <div v-if="data.item.gain_to_100_3 !== null"
                   :class="getFutureStyleClass(data.item.gain_to_100_3)"
                   class="trigger-cell-box mb-1">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <span class="badge-tag badge-tag-10 mr-2">10日</span>
                    <span class="trigger-gain font-monospace mr-2">{{ formatPct(data.item.gain_to_100_3) }}</span>
                  </div>
                  <span class="trigger-price font-monospace">{{ formatPrice(data.item.price_to_100_3) }}</span>
                </div>
              </div>
              <div v-if="data.item.gain_to_200_3 !== null"
                   :class="getFutureStyleClass(data.item.gain_to_200_3)"
                   class="trigger-cell-box">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <span class="badge-tag badge-tag-30 mr-2">30日</span>
                    <span class="trigger-gain font-monospace mr-2">{{ formatPct(data.item.gain_to_200_3) }}</span>
                  </div>
                  <span class="trigger-price font-monospace">{{ formatPrice(data.item.price_to_200_3) }}</span>
                </div>
              </div>
            </template>

            <!-- 实时幅/价 -->
            <template #cell(px_change_rate)="data">
              <div class="font-weight-bold font-monospace cell-real-gain" :class="getColorClass(data.item.px_change_rate)">
                {{ formatPct(data.item.px_change_rate) }}
              </div>
              <div class="small font-monospace cell-real-price" :class="getColorClass(data.item.px_change_rate)">
                {{ formatPrice(data.item.last_px) }}
              </div>
            </template>
           </b-table>
          </div>
        </div>

        <!-- Mobile View (移动端卡片视图) -->
        <div v-else class="p-2 bg-main" style="min-height: 100%;">
          <!-- 移动端复盘提示条 -->
          <div v-if="date1" class="replay-status-bar mb-2 p-2 rounded small" :class="statusBarThemeClass">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <span class="badge font-weight-bold" :class="statusBadgeClass">
                {{ statusBadgeText }}
              </span>
              <span class="text-muted" style="font-size: 0.75rem;">基准日: {{ baseTradeDate || latestTradeDate }}</span>
            </div>
            <div style="font-size: 0.78rem; line-height: 1.4;">
              <span v-if="isRealTrading">⚡ 盘中实时跟踪，动态测算今日（{{ targetTradeDate || date1 }}）各股票距异动空间。</span>
              <span v-else-if="isForecast">🎯 目标交易日（<strong>{{ targetTradeDate || date1 }}</strong>）开盘前预警测算。</span>
              <span v-else>📜 以 <strong>{{ baseTradeDate || date1 }}</strong> 收盘推演次日（<strong>{{ targetTradeDate }}</strong>）日K收盘验证。</span>
            </div>
          </div>

          <div v-for="(item, idx) in mergedItems" :key="idx" class="card border shadow-sm mb-2 rounded-lg">
             <div class="card-body p-2">
                <!-- 头部：股票名称、代码与当前价格 -->
                <div class="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                   <div @click="openSidebar(item)" class="cursor-pointer">
                      <span class="font-weight-bold mr-2 stock-name-row" :class="item.is_monitored ? 'text-danger' : 'text-main'">
                        {{ item.symbol_name }}
                        <span v-if="item.is_monitored" class="badge badge-danger ml-1" style="font-size: 10px; padding: 2px 4px; vertical-align: text-top;" v-b-tooltip.hover.top="disclaimerText">异</span>
                        <span v-else-if="item.monitor_expired" class="badge badge-secondary ml-1" style="font-size: 10px; padding: 2px 4px; vertical-align: text-top;" v-b-tooltip.hover.top="disclaimerText">异</span>
                      </span>
                      <span class="stock-code-text font-monospace">{{ item.symbol_code }}</span>
                      <div v-if="item.is_monitored || item.monitor_expired" class="small mt-1" :class="item.is_monitored ? 'text-danger' : 'text-muted'">
                         {{ item.monitor_start_date }}~{{ item.monitor_end_date }}
                         <span v-if="item.monitor_expired">(已到期)</span>
                      </div>
                   </div>
                   <div class="text-right">
                      <div class="font-weight-bold font-monospace cell-real-gain" :class="getColorClass(item.px_change_rate)">
                        {{ formatPct(item.px_change_rate) }}
                      </div>
                      <div class="small font-monospace cell-real-price" :class="getColorClass(item.px_change_rate)">
                        {{ formatPrice(item.last_px) }}
                      </div>
                   </div>
                </div>

                <!-- 异动1 (10日/100%) -->
                <div v-if="item.days1" class="bg-muted p-2 rounded mb-2 border">
                   <div class="d-flex justify-content-between align-items-center small mb-1">
                      <div class="d-flex align-items-center">
                        <span class="badge-tag badge-tag-10 mr-1">10日异动</span>
                        <span class="cell-sub-val font-monospace">{{ item.days1 }}天</span>
                      </div>
                      <div class="font-monospace">
                        <span class="text-muted mr-1">累计:</span>
                        <span class="font-weight-bold cell-main-val">{{ formatPct(item.gain100) }}</span>
                      </div>
                   </div>
                   <div class="d-flex justify-content-between align-items-center bg-card p-1 rounded border" style="font-size: 0.85rem;">
                      <div class="text-center px-1" style="flex: 1.2;">
                        <div class="text-muted small" style="font-size: 0.72rem;">{{ triggerTodayShortLabel }}</div>
                        <div :class="getTodayStyleClass(item.gain_to_100, item.px_change_rate, item.symbol_code)" class="rounded px-1 py-1">
                          <div class="font-weight-bold font-monospace">{{ formatPct(item.gain_to_100) }}</div>
                          <div class="trigger-diff-badge font-monospace" :class="getDiffClass(item.gain_to_100, item.px_change_rate, item.symbol_code)">
                            {{ getDiffText(item.gain_to_100, item.px_change_rate, item.symbol_code) }}
                          </div>
                        </div>
                      </div>
                      <div class="text-center border-left px-1" style="flex: 1;">
                        <div class="text-muted small" style="font-size: 0.72rem;">T+2日触发</div>
                        <div :class="getFutureStyleClass(item.gain_to_100_2)" class="rounded px-1 py-1">
                          <div class="font-weight-bold font-monospace">{{ formatPct(item.gain_to_100_2) }}</div>
                          <div class="trigger-price font-monospace">{{ formatPrice(item.price_to_100_2) }}</div>
                        </div>
                      </div>
                      <div class="text-center border-left px-1" style="flex: 1;">
                        <div class="text-muted small" style="font-size: 0.72rem;">T+3日触发</div>
                        <div :class="getFutureStyleClass(item.gain_to_100_3)" class="rounded px-1 py-1">
                          <div class="font-weight-bold font-monospace">{{ formatPct(item.gain_to_100_3) }}</div>
                          <div class="trigger-price font-monospace">{{ formatPrice(item.price_to_100_3) }}</div>
                        </div>
                      </div>
                   </div>
                </div>

                <!-- 异动2 (30日/200%) -->
                <div v-if="item.days2" class="bg-muted p-2 rounded border">
                   <div class="d-flex justify-content-between align-items-center small mb-1">
                      <div class="d-flex align-items-center">
                        <span class="badge-tag badge-tag-30 mr-1">30日异动</span>
                        <span class="cell-sub-val font-monospace">{{ item.days2 }}天</span>
                      </div>
                      <div class="font-monospace">
                        <span class="text-muted mr-1">累计:</span>
                        <span class="font-weight-bold cell-main-val">{{ formatPct(item.gain200) }}</span>
                      </div>
                   </div>
                   <div class="d-flex justify-content-between align-items-center bg-card p-1 rounded border" style="font-size: 0.85rem;">
                      <div class="text-center px-1" style="flex: 1.2;">
                        <div class="text-muted small" style="font-size: 0.72rem;">{{ triggerTodayShortLabel }}</div>
                        <div :class="getTodayStyleClass(item.gain_to_200, item.px_change_rate, item.symbol_code)" class="rounded px-1 py-1">
                          <div class="font-weight-bold font-monospace">{{ formatPct(item.gain_to_200) }}</div>
                          <div class="trigger-diff-badge font-monospace" :class="getDiffClass(item.gain_to_200, item.px_change_rate, item.symbol_code)">
                            {{ getDiffText(item.gain_to_200, item.px_change_rate, item.symbol_code) }}
                          </div>
                        </div>
                      </div>
                      <div class="text-center border-left px-1" style="flex: 1;">
                        <div class="text-muted small" style="font-size: 0.72rem;">T+2日触发</div>
                        <div :class="getFutureStyleClass(item.gain_to_200_2)" class="rounded px-1 py-1">
                          <div class="font-weight-bold font-monospace">{{ formatPct(item.gain_to_200_2) }}</div>
                          <div class="trigger-price font-monospace">{{ formatPrice(item.price_to_200_2) }}</div>
                        </div>
                      </div>
                      <div class="text-center border-left px-1" style="flex: 1;">
                        <div class="text-muted small" style="font-size: 0.72rem;">T+3日触发</div>
                        <div :class="getFutureStyleClass(item.gain_to_200_3)" class="rounded px-1 py-1">
                          <div class="font-weight-bold font-monospace">{{ formatPct(item.gain_to_200_3) }}</div>
                          <div class="trigger-price font-monospace">{{ formatPrice(item.price_to_200_3) }}</div>
                        </div>
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
    <StockInfoSidebar :symbol_code="sidebarSymbolCode" :symbol_name="sidebarSymbolName" :date_mark="date1"></StockInfoSidebar>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import WxShare from '@/components/share/WxShare.vue'
import moment from 'moment'

export default {
  components: {
    StockInfoSidebar: () => import('components/stock/StockInfoSidebar.vue'),
    WxShare
  },
  data(){
    return {
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      disclaimerText: '免责声明:带标记"异"的股票来自沪深交易所的交易信息披露。根据交易所规则“股票交易出现严重异动波动情形后10个交易日内，连续竞价阶段1分钟内单向申报买入（卖出）单只严重异常波动股票金额巨大的，交易所对有关交易行为予以重点监控。"',
      isGeneratingScreenshot: false,
      date1: '',
      latestTradeDate: '',
      nextForecastTradeDate: '',
      baseTradeDate: '',
      targetTradeDateFromServer: '',
      withRealStatus: null,
      loading: false,
      filterType: 'all',
      filterOptions: [
        { text: '全部', value: 'all' },
        { text: '只要异动', value: 'monitored' },
        { text: '排除异动', value: 'not_monitored' }
      ],
      items: [],
      windowWidth: window.innerWidth
    }
  },
  watch: {
    date1(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchList()
      }
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768
    },
    maxSelectableDate() {
      return this.nextForecastTradeDate || this.targetTradeDate || moment(this.latestTradeDate || undefined).add(10, 'days').format('YYYY-MM-DD')
    },
    isLatestDate() {
      if (!this.date1 || !this.maxSelectableDate) return false
      return this.date1 >= this.maxSelectableDate
    },
    isRealTrading() {
      if (this.withRealStatus !== null && this.withRealStatus !== undefined) {
        return this.withRealStatus === 1
      }
      return this.isTradingHours && this.date1 === moment().format('YYYY-MM-DD')
    },
    isForecast() {
      if (this.withRealStatus !== null && this.withRealStatus !== undefined) {
        return this.withRealStatus === -1 || this.withRealStatus === -2
      }
      return this.latestTradeDate ? this.date1 > this.latestTradeDate : false
    },
    isHistory() {
      if (this.withRealStatus !== null && this.withRealStatus !== undefined) {
        return this.withRealStatus === -3
      }
      return this.latestTradeDate ? this.date1 < this.latestTradeDate : false
    },
    statusBarThemeClass() {
      if (this.isRealTrading) return 'replay-status-realtime'
      if (this.isForecast) return 'replay-status-latest'
      return 'replay-status-history'
    },
    statusBadgeText() {
      if (this.isRealTrading) return '盘中实时'
      if (this.isForecast) return '开盘前预警推演'
      return '历史日K回放'
    },
    statusBadgeClass() {
      if (this.isRealTrading) return 'badge-success'
      if (this.isForecast) return 'badge-primary'
      return 'badge-info'
    },
    targetTradeDate() {
      if (this.targetTradeDateFromServer) {
        return this.targetTradeDateFromServer
      }
      if (this.items && this.items.length > 0 && this.items[0].target_trade_date) {
        return this.items[0].target_trade_date
      }
      if (!this.date1) return ''
      const m = moment(this.date1)
      if (m.day() === 5) {
        return m.add(3, 'days').format('YYYY-MM-DD')
      }
      return m.add(1, 'days').format('YYYY-MM-DD')
    },
    shortTargetDate() {
      if (!this.targetTradeDate) return ''
      return this.targetTradeDate.slice(5)
    },
    isTradingHours() {
      const now = moment()
      const day = now.day()
      if (day === 0 || day === 6) return false
      const timeStr = now.format('HH:mm:ss')
      return timeStr >= '09:15:00' && timeStr <= '15:05:00'
    },
    triggerTodayLabel() {
      if (this.isRealTrading) {
        return '今日触发 (实时)'
      }
      if (this.isForecast) {
        return `目标日触发 (${this.shortTargetDate || 'T+1'})`
      }
      return `次日触发 (${this.shortTargetDate || 'T+1'})`
    },
    triggerTodayShortLabel() {
      if (this.isRealTrading) return '今日触发(实时)'
      if (this.isForecast) return `目标触发(${this.shortTargetDate})`
      return `次日触发(${this.shortTargetDate})`
    },
    realPriceLabel() {
      if (this.isRealTrading) {
        return '实时(幅/价)'
      }
      if (this.isHistory) {
        return `次日实况 (${this.shortTargetDate})`
      }
      return `基准收盘 (${this.baseTradeDate ? this.baseTradeDate.slice(5) : '最新'})`
    },
    dynamicFields() {
      return [
        { key: 'symbol', label: '股票', sortable: false, thStyle: { width: '13%' } },
        { key: 'gain', label: '累计涨幅', sortable: false, class: 'text-right', thClass: 'text-right', thStyle: { width: '11%' } },
        { key: 'days', label: '天数', sortable: false, class: 'text-center', thClass: 'text-center', thStyle: { width: '5%' } },
        { 
          key: 'trigger_today', 
          label: this.triggerTodayLabel, 
          sortable: false, 
          thStyle: { width: '28%' }
        },
        { 
          key: 'trigger_tomorrow', 
          label: 'T+2日触发', 
          sortable: false, 
          thStyle: { width: '17%' }
        },
        { 
          key: 'trigger_day_after', 
          label: 'T+3日触发', 
          sortable: false, 
          thStyle: { width: '16%' }
        },
        { key: 'px_change_rate', label: this.realPriceLabel, sortable: true, class: 'text-right', thClass: 'text-right', thStyle: { width: '10%' } }
      ]
    },
    mergedItems() {
      let items = this.items
      if (this.filterType === 'monitored') {
        items = items.filter(item => item.is_monitored || item.monitor_expired)
      } else if (this.filterType === 'not_monitored') {
        items = items.filter(item => !item.is_monitored && !item.monitor_expired)
      }

      return items.map(item => {
        const hasT1 = item.t1_threshold_price > 0;
        const hasT2 = item.t2_threshold_price > 0;
        
        return {
          ...item,
          gain100: hasT1 ? item.t1_gain_pct : null,
          days1: hasT1 ? item.t1_days : null,
          dist100: hasT1 ? item.t1_space_pct : null,
          gain_to_100: hasT1 ? item.t1_threshold_gain_pct : null,
          price_to_100: hasT1 ? item.t1_threshold_price : null,
          gain_to_100_2: hasT1 ? item.t1_threshold_gain_pct2 : null,
          price_to_100_2: hasT1 ? item.t1_threshold_price2 : null,
          gain_to_100_3: hasT1 ? item.t1_threshold_gain_pct3 : null,
          price_to_100_3: hasT1 ? item.t1_threshold_price3 : null,
          
          gain200: hasT2 ? item.t2_gain_pct : null,
          days2: hasT2 ? item.t2_days : null,
          dist200: hasT2 ? item.t2_space_pct : null,
          gain_to_200: hasT2 ? item.t2_threshold_gain_pct : null,
          price_to_200: hasT2 ? item.t2_threshold_price : null,
          gain_to_200_2: hasT2 ? item.t2_threshold_gain_pct2 : null,
          price_to_200_2: hasT2 ? item.t2_threshold_price2 : null,
          gain_to_200_3: hasT2 ? item.t2_threshold_gain_pct3 : null,
          price_to_200_3: hasT2 ? item.t2_threshold_price3 : null,
          
          last_px: item.last_px,
          px_change_rate: item.px_change_rate
        }
      }).sort((a, b) => {
        const getSortVal = (item) => {
           let v1 = item.gain_to_100;
           let v2 = item.gain_to_200;
           
           v1 = (v1 !== null && v1 !== undefined && v1 !== '') ? Number(v1) : Infinity;
           v2 = (v2 !== null && v2 !== undefined && v2 !== '') ? Number(v2) : Infinity;
           
           if (isNaN(v1)) v1 = Infinity;
           if (isNaN(v2)) v2 = Infinity;

           const minVal = Math.min(v1, v2);
           if (minVal === Infinity) return Infinity;
           
           let pxChange = item.px_change_rate;
           pxChange = (pxChange !== null && pxChange !== undefined && pxChange !== '') ? Number(pxChange) : 0;
           if (isNaN(pxChange)) pxChange = 0;
           
           return minVal - pxChange;
        }

        const valA = getSortVal(a);
        const valB = getSortVal(b);

        return valA - valB;
      })
    }
  },
  mounted(){
    this.setDefaultTradeDateByRule()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    stepTradeDate(direction) {
      if (this.loading || !this.date1) return
      if (direction === -1) {
        const prevDay = moment(this.date1).subtract(1, 'days').format('YYYY-MM-DD')
        this.loading = true
        request_zzquant({
          url: '/v3/market/trade/days',
          params: { days: 1, day_end: prevDay }
        }).then(res => {
          this.loading = false
          if (res.data && res.data.code === 20000 && Array.isArray(res.data.data) && res.data.data.length > 0) {
            this.date1 = res.data.data[0]
          }
        }).catch(() => {
          this.loading = false
        })
      } else if (direction === 1) {
        if (this.isLatestDate) return
        // 若当前在最新已收盘交易日，且已知下一个交易日，直接步进到下一个交易日 (例如 2026-10-08)
        if (this.nextForecastTradeDate && this.date1 === this.latestTradeDate) {
          this.date1 = this.nextForecastTradeDate
          return
        }
        const nextDay = moment(this.date1).add(1, 'days').format('YYYY-MM-DD')
        this.loading = true
        request_zzquant({
          url: '/v3/market/trade/days',
          params: { day_start: nextDay, days: 1 }
        }).then(res => {
          this.loading = false
          if (res.data && res.data.code === 20000 && Array.isArray(res.data.data) && res.data.data.length > 0) {
            const targetD = res.data.data[0]
            if (this.maxSelectableDate && targetD > this.maxSelectableDate) {
              this.date1 = this.maxSelectableDate
            } else {
              this.date1 = targetD
            }
          } else if (this.nextForecastTradeDate) {
            this.date1 = this.nextForecastTradeDate
          }
        }).catch(() => {
          this.loading = false
        })
      }
    },
    openSidebar(item) {
      this.sidebarSymbolCode = item.symbol_code
      this.sidebarSymbolName = item.symbol_name
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
    },
    setDefaultTradeDateByRule() {
      const todayStr = moment().utcOffset(8).format('YYYY-MM-DD')
      const futureEnd = moment().utcOffset(8).add(20, 'days').format('YYYY-MM-DD')
      
      // 清理旧版本可能存在的脏缓存
      try {
        sessionStorage.removeItem('trade_date_' + futureEnd)
      } catch (e) {
        // ignore cache removal error
      }

      this.loading = true
      request_zzquant({
        url: '/v3/market/trade/days',
        params: { days: 15, day_end: futureEnd }
      }).then(res => {
        if (res.data && res.data.code === 20000 && Array.isArray(res.data.data) && res.data.data.length > 0) {
          const days = res.data.data
          const pastDays = days.filter(d => d <= todayStr)
          const latest = pastDays.length > 0 ? pastDays[pastDays.length - 1] : days[0]
          this.latestTradeDate = latest
          this.baseTradeDate = latest

          // 计算或获取紧随 latest 之后的下一个交易日 (例如 2026-10-08)
          const latestIdx = days.indexOf(latest)
          if (latestIdx !== -1 && latestIdx + 1 < days.length) {
            this.nextForecastTradeDate = days[latestIdx + 1]
          } else {
            this.nextForecastTradeDate = moment(latest).add(latest.endsWith('Fri') ? 3 : 1, 'days').format('YYYY-MM-DD')
          }

          // 始终默认选择最新已收盘交易日（如 2026-09-30），同时放开向右步进到未来交易日
          this.date1 = latest
        } else {
          this.date1 = moment().format('YYYY-MM-DD')
          this.latestTradeDate = this.date1
          this.baseTradeDate = this.date1
          this.nextForecastTradeDate = moment().add(1, 'days').format('YYYY-MM-DD')
        }
      }).catch(() => {
        this.date1 = moment().format('YYYY-MM-DD')
        this.latestTradeDate = this.date1
        this.baseTradeDate = this.date1
        this.nextForecastTradeDate = moment().add(1, 'days').format('YYYY-MM-DD')
      })
    },
    fetchList(){
      this.loading = true
      request_zzquant({
        url: '/v3/market/movement/alerts',
        params: { date1: this.date1, type: 0, limit: 200, is_real: 1 }
      }).then(res=>{
        this.loading = false
        if (res.data.code === 20000) {
          this.items = res.data.data || []
          if (this.items.length > 0) {
            const first = this.items[0]
            if (first.target_trade_date) {
              this.targetTradeDateFromServer = first.target_trade_date
            }
            if (first.base_trade_date) {
              this.baseTradeDate = first.base_trade_date
            }
            if (first.with_real_status !== undefined) {
              this.withRealStatus = first.with_real_status
            }
          }
        }
      }).catch(()=>{
        this.loading = false
      })
    },
    formatPrice(v){
      if (v===null || v===undefined) return '--'
      return Number(v).toFixed(2)
    },
    formatPct(v){
      if (v===null || v===undefined) return '--'
      const num = Number(v)
      return (num > 0 ? '+' : '') + num.toFixed(2) + '%'
    },
    getColorClass(val){
      if (val > 0) return 'text-up-bright'
      if (val < 0) return 'text-down-bright'
      return 'text-muted'
    },
    getMaxLimitPct(code) {
      if (!code) return 10
      const c = String(code).trim()
      if (c.startsWith('688') || c.startsWith('300') || c.startsWith('301')) {
        return 20
      }
      if (c.startsWith('43') || c.startsWith('83') || c.startsWith('87') || c.startsWith('92')) {
        return 30
      }
      return 10
    },
    getAlertStatus(threshold, real, code) {
      if (threshold === null || threshold === undefined || real === null || real === undefined) {
        return { type: 'none', text: '--', badgeClass: 'badge-diff-normal', pillClass: 'pill-normal', tooltip: '' }
      }
      const t = Number(threshold)
      const r = Number(real)
      const diff = t - r
      const maxLimit = this.getMaxLimitPct(code)

      // 1. 已达异动/已触及
      if (diff <= 0) {
        return {
          type: 'danger',
          text: '已触及',
          badgeClass: 'badge-diff-danger',
          pillClass: 'pill-danger',
          tooltip: '已达到或突破触发涨幅'
        }
      }

      // 2. 超出当日涨停极限（当日绝无可能触发）
      // 容差1.0%：如创业板阈值20.74%紧贴20%涨停板且离当前涨幅很近(diff<=3)时除外
      const isNearLimitHigh = (t <= maxLimit + 1.0) && (diff <= 3.0)
      if (t > maxLimit && !isNearLimitHigh) {
        return {
          type: 'safe',
          text: '安全',
          badgeClass: 'badge-diff-safe',
          pillClass: 'pill-normal',
          tooltip: `触发涨幅(${t.toFixed(2)}%)超出单日涨停上限(+${maxLimit}%)，单日绝无触发可能`
        }
      }

      // 3. 临界高危预警 (差额 <= 3%)
      if (diff <= 3) {
        return {
          type: 'warning-high',
          text: `距 ${diff.toFixed(2)}%`,
          badgeClass: 'badge-diff-warning-high',
          pillClass: 'pill-warning-high',
          tooltip: `距离触发异动仅差 ${diff.toFixed(2)} 个百分点`
        }
      }

      // 4. 处于涨幅可达区间 (若股票日内大跌导致差额超出日内最大波动跨度，也归为安全)
      if (diff > maxLimit * 1.5) {
        return {
          type: 'safe',
          text: '安全',
          badgeClass: 'badge-diff-safe',
          pillClass: 'pill-normal',
          tooltip: `差距过大(${diff.toFixed(2)}%)，处于安全区间`
        }
      }

      // 5. 观察区 (在涨停板以内，有触及可能)
      return {
        type: 'warning-mild',
        text: `距 ${diff.toFixed(2)}%`,
        badgeClass: 'badge-diff-warning-mild',
        pillClass: 'pill-warning-mild',
        tooltip: `在涨停可达范围内，距触发差 ${diff.toFixed(2)}%`
      }
    },
    getDiffText(threshold, real, code) {
      return this.getAlertStatus(threshold, real, code).text
    },
    getDiffClass(threshold, real, code) {
      return this.getAlertStatus(threshold, real, code).badgeClass
    },
    getDiffTooltip(threshold, real, code) {
      return this.getAlertStatus(threshold, real, code).tooltip
    },
    getTodayStyleClass(threshold, real, code) {
      return this.getAlertStatus(threshold, real, code).pillClass
    },
    getFutureStyleClass(dist) {
      if (dist === null || dist === undefined) return 'pill-normal'
      const v = Number(dist)
      if (v <= 0) return 'pill-danger'
      if (v <= 3) return 'pill-warning-high'
      if (v < 10) return 'pill-warning-mild'
      return 'pill-normal'
    },
    getWeekDay(dateStr) {
      if (!dateStr) return ''
      const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      return days[new Date(dateStr).getDay()]
    },
    async downloadScreenshot() {
      if (this.isGeneratingScreenshot) return
      this.isGeneratingScreenshot = true
      
      try {
        const element = this.$refs.tableContainer
        if (!element) return

        const watermark = document.createElement('div')
        watermark.innerHTML = '自在量化<br>quant.zizizaizai.com'
        watermark.style.position = 'fixed'
        watermark.style.top = '50%'
        watermark.style.left = '50%'
        watermark.style.transform = 'translate(-50%, -50%) rotate(-30deg)'
        watermark.style.fontSize = '40px'
        watermark.style.color = 'rgba(150, 150, 150, 0.4)'
        watermark.style.zIndex = '9999'
        watermark.style.pointerEvents = 'none'
        watermark.style.textAlign = 'center'
        watermark.style.fontWeight = 'bold'
        element.appendChild(watermark)

        const html2canvas = (await import('html2canvas')).default
        const canvas = await html2canvas(element, {
          useCORS: true,
          scale: 2,
          backgroundColor: '#ffffff'
        })
        
        element.removeChild(watermark)
        
        const link = document.createElement('a')
        link.download = `异动提醒_${this.date1}.png`
        link.href = canvas.toDataURL('image/png')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
      } catch (error) {
        console.error('Screenshot failed:', error)
        this.$bvToast.toast('生成截图失败，请重试', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isGeneratingScreenshot = false
      }
    }
  }
}
</script>

<style>
/* 悬浮表头与暗黑适配 */
.sticky-header-table > thead > tr > th {
  position: sticky !important;
  top: 0;
  z-index: 100;
  background-color: var(--bg-muted) !important;
  box-shadow: 0 1px 0 var(--border-color);
  border-top: none !important;
  color: var(--text-main) !important;
  padding: 8px 10px !important;
  vertical-align: middle !important;
  font-size: 13px !important;
  font-weight: 600 !important;
}

.movement-table td {
  padding: 6px 10px !important;
  vertical-align: middle !important;
}

/* 复盘提示条样式 */
.replay-status-bar {
  border: 1px solid var(--border-color);
  font-size: 0.82rem;
  line-height: 1.5;
  transition: all 0.2s ease;
}
.replay-status-history {
  background-color: rgba(23, 162, 184, 0.08);
  border-color: rgba(23, 162, 184, 0.25);
  color: var(--text-main);
}
.replay-status-latest {
  background-color: rgba(13, 110, 253, 0.06);
  border-color: rgba(13, 110, 253, 0.2);
  color: var(--text-main);
}
.replay-status-realtime {
  background-color: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.25);
  color: var(--text-main);
}
.py-1-5 {
  padding-top: 0.375rem !important;
  padding-bottom: 0.375rem !important;
}

/* 股票信息样式 */
.stock-name-row {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-main);
}
.stock-code-text {
  font-size: 12px;
  color: var(--text-muted);
}

/* 数值对比度与字号增强 */
.cell-main-val {
  font-size: 13px;
  color: var(--text-main);
}
.cell-sub-val {
  font-size: 12px;
  color: var(--text-muted);
}
.cell-real-gain {
  font-size: 14px;
  font-weight: 700;
}
.cell-real-price {
  font-size: 12px;
}

/* 涨跌鲜亮颜色适配 */
.text-up-bright {
  color: #ef4444 !important;
}
.text-down-bright {
  color: #10b981 !important;
}
body.theme-dark .text-up-bright {
  color: #f87171 !important;
}
body.theme-dark .text-down-bright {
  color: #4ade80 !important;
}

/* 顶部交易日步进器 */
.movement-date-stepper {
  background-color: var(--bg-muted);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 2px 4px;
}
.movement-date-btn {
  background: transparent;
  border: none;
  color: var(--text-muted);
  padding: 3px 8px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}
.movement-date-btn:hover:not(:disabled) {
  background-color: var(--bg-card);
  color: var(--text-main);
}
.movement-date-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.movement-datepicker {
  background: transparent !important;
}
.movement-datepicker button {
  background: transparent !important;
  border: none !important;
  color: var(--text-main) !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  padding: 2px 6px !important;
}
body.theme-dark .movement-datepicker button {
  color: #f1f5f9 !important;
}

/* 图例指示小圆点 */
.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}
.dot-danger { background-color: #ef4444; }
.dot-warning-high { background-color: #f59e0b; }
.dot-warning-mild { background-color: #eab308; }
.dot-safe { background-color: #94a3b8; }

/* 10日与30日微标标签 (鲜艳对比度) */
.badge-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 5px;
  font-size: 11px;
  border-radius: 3px;
  font-weight: 600;
  line-height: 1.3;
}
.badge-tag-10 {
  background: rgba(59, 130, 246, 0.18);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.4);
}
body.theme-dark .badge-tag-10 {
  background: rgba(59, 130, 246, 0.25);
  color: #60a5fa;
  border-color: rgba(96, 165, 250, 0.5);
}

.badge-tag-30 {
  background: rgba(168, 85, 247, 0.18);
  color: #a855f7;
  border: 1px solid rgba(168, 85, 247, 0.4);
}
body.theme-dark .badge-tag-30 {
  background: rgba(168, 85, 247, 0.25);
  color: #c084fc;
  border-color: rgba(192, 132, 252, 0.5);
}

/* 单元格微型信息条块 */
.trigger-cell-box {
  padding: 4px 8px;
  border-radius: 5px;
  border: 1px solid transparent;
  transition: all 0.15s ease;
}

.trigger-gain {
  font-weight: 700;
  font-size: 13px;
  line-height: 1.2;
}

.trigger-price {
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.2;
}
body.theme-dark .trigger-price {
  color: #cbd5e1 !important;
}

/* 距触发空间徽章 */
.trigger-diff-badge {
  font-size: 11.5px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

.badge-diff-danger {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.4);
}
body.theme-dark .badge-diff-danger {
  color: #f87171;
  background: rgba(239, 68, 68, 0.25);
}

.badge-diff-warning-high {
  background: rgba(245, 158, 11, 0.2);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.45);
}
body.theme-dark .badge-diff-warning-high {
  color: #fbbf24;
  background: rgba(245, 158, 11, 0.25);
}

.badge-diff-warning-mild {
  background: rgba(234, 179, 8, 0.15);
  color: #ca8a04;
  border: 1px solid rgba(234, 179, 8, 0.35);
}
body.theme-dark .badge-diff-warning-mild {
  color: #fde047;
  background: rgba(234, 179, 8, 0.2);
  border-color: rgba(253, 224, 71, 0.35);
}

.badge-diff-safe {
  background: rgba(100, 116, 139, 0.08);
  color: var(--text-muted);
  border: 1px solid rgba(100, 116, 139, 0.2);
}
body.theme-dark .badge-diff-safe {
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.1);
  border-color: rgba(148, 163, 184, 0.2);
}

.badge-diff-normal {
  background: rgba(100, 116, 139, 0.1);
  color: var(--text-muted);
}
body.theme-dark .badge-diff-normal {
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.12);
}

/* 预警等级梯队配色 (清爽高对比度) */
.pill-danger {
  background-color: rgba(239, 68, 68, 0.12) !important;
  border-color: rgba(239, 68, 68, 0.35) !important;
}
.pill-danger .trigger-gain {
  color: #ef4444 !important;
}
body.theme-dark .pill-danger {
  background-color: rgba(239, 68, 68, 0.2) !important;
  border-color: rgba(239, 68, 68, 0.45) !important;
}
body.theme-dark .pill-danger .trigger-gain {
  color: #f87171 !important;
}

.pill-warning-high {
  background-color: rgba(245, 158, 11, 0.12) !important;
  border-color: rgba(245, 158, 11, 0.35) !important;
}
.pill-warning-high .trigger-gain {
  color: #d97706 !important;
}
body.theme-dark .pill-warning-high {
  background-color: rgba(245, 158, 11, 0.18) !important;
  border-color: rgba(245, 158, 11, 0.45) !important;
}
body.theme-dark .pill-warning-high .trigger-gain {
  color: #fbbf24 !important;
}

.pill-warning-mild {
  background-color: rgba(234, 179, 8, 0.06) !important;
  border-color: rgba(234, 179, 8, 0.2) !important;
}
.pill-warning-mild .trigger-gain {
  color: #ca8a04 !important;
}
body.theme-dark .pill-warning-mild {
  background-color: rgba(234, 179, 8, 0.1) !important;
  border-color: rgba(234, 179, 8, 0.3) !important;
}
body.theme-dark .pill-warning-mild .trigger-gain {
  color: #fde047 !important;
}

.pill-normal {
  background-color: rgba(100, 116, 139, 0.04);
  border-color: transparent;
}
.pill-normal .trigger-gain {
  color: var(--text-main);
}
body.theme-dark .pill-normal .trigger-gain {
  color: #f1f5f9;
}

.font-monospace {
  font-family: 'IBM Plex Mono', 'Consolas', monospace, sans-serif;
}
</style>
